/**
 * DashboardPage - Main dashboard overview
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Card, CardHeader, CardTitle, CardContent, Badge, Spinner } from '../components/ui';
import { useCalendarStore, useAuthStore, useWebshopStore, useWasserijStore } from '../stores';
import { useUserRole } from '../hooks/useUserRole';
import { CallStatsWidget } from '../components/phone/CallStatsWidget';
import type { RouteListItem } from '../lib/salesforce';

const CalendarIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const RouteIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

const TruckIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ShoppingBagIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
  </svg>
);

const WasserijIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
  </svg>
);

export function DashboardPage() {
  const { user } = useAuthStore();
  const { role } = useUserRole();
  const isAdmin = role === 'admin';
  const { routesByDate, isLoading, fetchRoutes } = useCalendarStore();
  const { orderCounts, fetchOrders } = useWebshopStore();
  const { pipeline, pipelineTotal, fetchPipeline } = useWasserijStore();

  // Fetch routes, webshop orders, and wasserij on mount
  useEffect(() => {
    fetchRoutes();
    fetchOrders();
    if (isAdmin) fetchPipeline();
  }, [fetchRoutes, fetchOrders, fetchPipeline, isAdmin]);

  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');

  // Get today's routes from the routesByDate map
  const todayRoutes = useMemo(() => {
    return routesByDate[todayStr] || [];
  }, [routesByDate, todayStr]);

  const completedToday = todayRoutes.filter((r: RouteListItem) => r.status === 'Afgerond').length;
  const inProgressToday = todayRoutes.filter((r: RouteListItem) => r.status === 'Onderweg').length;

  // Get display name from user
  const displayName = user?.displayName?.split(' ')[0] || '';

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-heading font-semibold text-main">
            Welkom{displayName ? `, ${displayName}` : ''}
          </h1>
          <p className="text-muted">
            {format(today, 'EEEE d MMMM yyyy', { locale: nl })}
          </p>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card variant="elevated" className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent/10 rounded-md flex items-center justify-center text-accent">
                <RouteIcon />
              </div>
              <div>
                <p className="text-sm text-muted">Routes vandaag</p>
                <p className="text-2xl font-semibold text-main">
                  {isLoading ? '-' : todayRoutes.length}
                </p>
              </div>
            </div>
          </Card>

          <Card variant="elevated" className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-success/10 rounded-md flex items-center justify-center text-success">
                <CheckCircleIcon />
              </div>
              <div>
                <p className="text-sm text-muted">Afgerond</p>
                <p className="text-2xl font-semibold text-main">
                  {isLoading ? '-' : completedToday}
                </p>
              </div>
            </div>
          </Card>

          <Card variant="elevated" className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-warning/10 rounded-md flex items-center justify-center text-warning">
                <TruckIcon />
              </div>
              <div>
                <p className="text-sm text-muted">Onderweg</p>
                <p className="text-2xl font-semibold text-main">
                  {isLoading ? '-' : inProgressToday}
                </p>
              </div>
            </div>
          </Card>

          <Link to="/webshop">
            <Card variant="elevated" className="p-6 hover:shadow-card transition-shadow h-full">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/10 rounded-md flex items-center justify-center text-amber-600">
                  <ShoppingBagIcon />
                </div>
                <div>
                  <p className="text-sm text-muted">Webshop nieuw</p>
                  <p className="text-2xl font-semibold text-main">
                    {orderCounts.klaarmakenVerzending}
                  </p>
                </div>
              </div>
            </Card>
          </Link>
        </div>

        {/* Wasserij summary (admin only) */}
        {isAdmin && pipeline.length > 0 && (
          <Link to="/wasserij" className="block mb-8">
            <Card variant="bordered" className="p-5 hover:shadow-card transition-shadow">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-md flex items-center justify-center text-indigo-600">
                  <WasserijIcon />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-main">Wasserij</p>
                  <p className="text-sm text-muted">
                    {pipelineTotal.items.toLocaleString('nl-NL')} items &middot;{' '}
                    {pipeline.find(s => s.status === 'Klaar voor Reinigen')?.itemCount || 0} klaar voor reinigen &middot;{' '}
                    {pipeline.find(s => s.status === 'Drogen')?.itemCount || 0} aan het drogen &middot;{' '}
                    {pipeline.find(s => s.status === 'Ingepakt')?.itemCount || 0} ingepakt
                  </p>
                </div>
                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Card>
          </Link>
        )}

        {/* Call stats (admin only) */}
        {isAdmin && (
          <div className="mb-8">
            <CallStatsWidget />
          </div>
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link to="/kalender" className="block">
            <Card variant="bordered" className="p-6 hover:shadow-card transition-shadow group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-md flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-colors">
                  <CalendarIcon />
                </div>
                <div>
                  <h3 className="font-medium text-main group-hover:text-accent transition-colors">
                    Routes
                  </h3>
                  <p className="text-sm text-muted">
                    Bekijk alle routes per dag, week of maand
                  </p>
                </div>
              </div>
            </Card>
          </Link>

          <Link to="/route-planner" className="block">
            <Card variant="bordered" className="p-6 hover:shadow-card transition-shadow group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-md flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-colors">
                  <RouteIcon />
                </div>
                <div>
                  <h3 className="font-medium text-main group-hover:text-accent transition-colors">
                    Route Planner
                  </h3>
                  <p className="text-sm text-muted">
                    Plan en optimaliseer routes
                  </p>
                </div>
              </div>
            </Card>
          </Link>

          <Link to="/webshop" className="block">
            <Card variant="bordered" className="p-6 hover:shadow-card transition-shadow group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/10 rounded-md flex items-center justify-center text-amber-600 group-hover:bg-amber-500 group-hover:text-white transition-colors">
                  <ShoppingBagIcon />
                </div>
                <div>
                  <h3 className="font-medium text-main group-hover:text-amber-600 transition-colors">
                    Webshop
                  </h3>
                  <p className="text-sm text-muted">
                    Bestellingen verwerken
                  </p>
                </div>
              </div>
            </Card>
          </Link>
        </div>

        {/* Today's routes */}
        <Card variant="elevated" padding="none">
          <CardHeader className="px-6 pt-6">
            <CardTitle>Routes vandaag</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : todayRoutes.length === 0 ? (
              <p className="text-muted py-8 text-center">
                Geen routes gepland voor vandaag
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {todayRoutes.map((route: RouteListItem) => (
                  <Link
                    key={route.id}
                    to={`/route-planner?routeId=${route.id}`}
                    className="flex items-center justify-between py-4 hover:bg-gray-50 -mx-6 px-6 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center text-sm font-medium text-accent">
                        {route.chauffeurName?.split(' ').map((n: string) => n[0]).join('') || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-main">{route.name}</p>
                        <p className="text-sm text-muted">
                          {route.chauffeurName || 'Geen chauffeur'} • {route.stopCount} stops
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={
                        route.status === 'Afgerond'
                          ? 'success'
                          : route.status === 'Onderweg'
                            ? 'warning'
                            : route.locked
                              ? 'info'
                              : 'default'
                      }
                    >
                      {route.status || (route.locked ? 'Vergrendeld' : 'Gepland')}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
